type DateInput = string | number | Date;

function toDate(value: DateInput): Date {
  return value instanceof Date ? value : new Date(value);
}

/**
 * The browser's local IANA timezone identifier (e.g. "Europe/London", "America/New_York").
 * Use this to explicitly override IntlProvider's default timezone in FormattedDate/FormattedTime.
 */
export const clientTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

/**
 * Format a date in the server/venue timezone.
 * Use for event dates, order dates — anything that should display in the venue's local time.
 */
export function formatServerDate(
  value: DateInput,
  locale: string,
  serverTimezone: string,
  options?: Intl.DateTimeFormatOptions
): string {
  return toDate(value).toLocaleDateString(locale, {
    timeZone: serverTimezone,
    ...options,
  });
}

/**
 * Format a time in the server/venue timezone.
 */
export function formatServerTime(
  value: DateInput,
  locale: string,
  serverTimezone: string,
  options?: Intl.DateTimeFormatOptions
): string {
  return toDate(value).toLocaleTimeString(locale, {
    timeZone: serverTimezone,
    ...options,
  });
}

/**
 * Format a date in the user's local (browser) timezone.
 * Use for sale start/end dates where the user cares about their own time.
 */
export function formatLocalDate(
  value: DateInput,
  locale: string,
  options?: Intl.DateTimeFormatOptions
): string {
  return toDate(value).toLocaleDateString(locale, options);
}

/**
 * Format a time in the user's local (browser) timezone.
 */
export function formatLocalTime(
  value: DateInput,
  locale: string,
  options?: Intl.DateTimeFormatOptions
): string {
  return toDate(value).toLocaleTimeString(locale, options);
}

/**
 * Get the timezone abbreviation for the user's local timezone (e.g. "GMT", "CET").
 */
export function getLocalTimezoneAbbr(value: DateInput, locale: string): string {
  const parts = new Intl.DateTimeFormat(locale, {
    timeZoneName: 'short',
  }).formatToParts(toDate(value));
  return parts.find((p) => p.type === 'timeZoneName')?.value ?? '';
}

export type DateParts = { year: number; month: number; day: number };

const datePartsFormatters = new Map<string, Intl.DateTimeFormat>();
const timePartsFormatters = new Map<string, Intl.DateTimeFormat>();
const weekdayFormatters = new Map<string, Intl.DateTimeFormat>();

function getCachedFormatter(
  cache: Map<string, Intl.DateTimeFormat>,
  timeZone: string,
  options: Intl.DateTimeFormatOptions
): Intl.DateTimeFormat {
  let formatter = cache.get(timeZone);
  if (!formatter) {
    // 'en' locale data is always available, including under the app's forced
    // Intl polyfill which only ships data for the supported UI locales.
    formatter = new Intl.DateTimeFormat('en', { timeZone, ...options });
    cache.set(timeZone, formatter);
  }
  return formatter;
}

// Values are read via formatToParts, never by parsing a formatted string —
// the string's pattern depends on locale data and the Intl polyfill.
function numericPart(
  parts: Intl.DateTimeFormatPart[],
  type: Intl.DateTimeFormatPartTypes
): number {
  return Number(parts.find((p) => p.type === type)?.value ?? 0);
}

/**
 * The calendar date (year, month 1-12, day 1-31) an instant falls on in the
 * given IANA timezone. Use for any day-of-month math on event dates — never
 * read getDate()/getMonth() off a parsed event date, those use the browser
 * timezone and shift events across day/month boundaries for remote buyers.
 */
export function getDateParts(value: DateInput, timeZone: string): DateParts {
  const parts = getCachedFormatter(datePartsFormatters, timeZone, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(toDate(value));
  return {
    year: numericPart(parts, 'year'),
    month: numericPart(parts, 'month'),
    day: numericPart(parts, 'day'),
  };
}

/**
 * The English long weekday name (e.g. "Monday") an instant falls on in the
 * given IANA timezone. Matches the `DayOfWeek` values used by the events API,
 * so it can be compared against the weekday filter options directly.
 */
export function getServerWeekday(value: DateInput, timeZone: string): string {
  const parts = getCachedFormatter(weekdayFormatters, timeZone, {
    weekday: 'long',
  }).formatToParts(toDate(value));
  return parts.find((p) => p.type === 'weekday')?.value ?? '';
}

/**
 * The wall-clock time (hour 0-23, minute 0-59) of an instant in the given
 * IANA timezone.
 */
export function getTimeParts(
  value: DateInput,
  timeZone: string
): { hour: number; minute: number } {
  const parts = getCachedFormatter(timePartsFormatters, timeZone, {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(toDate(value));
  return {
    hour: numericPart(parts, 'hour'),
    minute: numericPart(parts, 'minute'),
  };
}

/**
 * A number that orders and compares calendar days (e.g. 20261130).
 */
export function dateKey({ year, month, day }: DateParts): number {
  return year * 10000 + month * 100 + day;
}

/**
 * Whether two instants fall on the same calendar day in the given timezone.
 */
export function isSameCalendarDay(
  a: DateInput,
  b: DateInput,
  timeZone: string
): boolean {
  return (
    dateKey(getDateParts(a, timeZone)) === dateKey(getDateParts(b, timeZone))
  );
}

/**
 * Milliseconds until a UTC datetime. Negative means the datetime is in the past.
 * Use for countdowns (e.g. for expiry) — no timezone conversion needed.
 */
export function timeUntilMs(isoString: string): number {
  return new Date(isoString).getTime() - Date.now();
}

/**
 * Check if a UTC datetime is in the past.
 */
export function isExpired(isoString: string): boolean {
  return timeUntilMs(isoString) <= 0;
}

/**
 * Whether an online sale hasn't opened yet. A missing sale start means no
 * restriction, so the sale counts as started. The value is already adjusted for
 * the caller's customer-tag early access by the backend, so no tag handling here.
 */
export function isSaleNotStarted(saleStarts?: string | null): boolean {
  return saleStarts ? timeUntilMs(saleStarts) > 0 : false;
}

/**
 * Seconds until a UTC datetime. Negative means the datetime is in the past.
 * Returns 0 when no datetime is given.
 */
export function secondsUntil(isoString?: string | null): number {
  return isoString ? timeUntilMs(isoString) / 1000 : 0;
}

/**
 * Whole minutes remaining until a UTC datetime, never negative. Rounded to the
 * nearest minute.
 */
export function minutesUntil(isoString?: string | null): number {
  return Math.max(0, Math.round(secondsUntil(isoString) / 60));
}
